<template>
  <FormLayout icon="cogs" title="Account Settings" @save="setAccountDetails">
    <b-loading is-full-page :active="isLoading" />
    <div class="mb-4 section-border">
      <h2 class="is-size-5">General Settings</h2>
      <hr />
      <FormLayoutSection
        :title="titles.allowPersonalTravel"
        :description="descriptions.allowPersonalTravel"
      >
        <b-radio
          v-model="accountDetails.allowPersonalTravel"
          :native-value="true"
          name="personal-travel"
        >
          Allow personal travel to be booked
        </b-radio>
        <b-radio
          v-model="accountDetails.allowPersonalTravel"
          :native-value="false"
          name="personal-travel"
        >
          Do not allow personal travel to be booked
        </b-radio>
      </FormLayoutSection>
      <FormLayoutSection :title="titles.selectPaidSeats" :description="descriptions.selectPaidSeats">
            <b-radio v-model="accountDetails.selectPaidSeats" native-value="Allow" name="select-paid-seats">
                Allow
            </b-radio>
            <b-radio v-model="accountDetails.selectPaidSeats" native-value="Disallow" name="select-paid-seats">
                Disallow
            </b-radio>
            
        </FormLayoutSection>
    </div>
    <div class="mb-4 section-border">
      <h2 class="is-size-5">Preferred Vendors</h2>
      <hr />
      <FormLayoutSection
        :title="titles.preferredAirVendors"
        :description="descriptions.preferredVendors"
      >
        <TagAutocomplete
          icon="airplane"
          :chainData="airlines"
          placeholder="Search by airline name"
          :selected="selectedPreferredAirVendors"
          @list="updatePreferredAirVendors"
        />
      </FormLayoutSection>
      <FormLayoutSection
        :title="titles.preferredCarVendors"
        :description="descriptions.preferredVendors"
      >
        <TagAutocomplete
          icon="car"
          :chainData="carProviders"
          :selected="selectedPreferredCarVendors"
          placeholder="Search by car rental company name"
          @list="updatePreferredCarVendors"
        />
      </FormLayoutSection>
      <FormLayoutSection
        :title="titles.preferredHotelVendors"
        :description="descriptions.preferredVendors"
      >
        <TagAutocomplete
          icon="bed"
          :chainData="hotelChains"
          placeholder="Search by hotel name"
          :selected="selectedPreferredHotelVendors"
          @list="updatePreferredHotelVendors"
        />
      </FormLayoutSection>
    </div>
    <div class="mb-4 section-border">
      <h2 class="is-size-5">Self-registration</h2>
      <hr />
      <FormLayoutSection :title="titles.enableSelfRegistration">
        <b-radio
          v-model="accountDetails.selfRegistration.isEnabled"
          :native-value="true"
          name="enable-self-registration"
        >
          Yes, enable self-registration
        </b-radio>
        <b-radio
          v-model="accountDetails.selfRegistration.isEnabled"
          :native-value="false"
          name="enable-self-registration"
        >
          No, do not allow self-registration
        </b-radio>
      </FormLayoutSection>
      <FormLayoutSection
        v-if="accountDetails.selfRegistration.isEnabled"
        :title="titles.defaultTravelPolicy"
      >
        <b-select
          v-model="accountDetails.selfRegistration.defaultTravelPolicyId"
          placeholder="Select Travel Policy"
        >
          <option value="">None</option>
          <option
            v-for="travelPolicy in travelPolicyList"
            :value="travelPolicy.id"
            :key="travelPolicy.id"
          >
            {{ travelPolicy.ruleClass }}
          </option>
        </b-select>
      </FormLayoutSection>
      <FormLayoutSection
        v-if="accountDetails.selfRegistration.isEnabled"
        :title="titles.enableJoinCode"
        subtitle="If turned on, anyone with this code can join your organization."
      >
        <b-radio
          v-model="accountDetails.selfRegistration.isJoinCodeEnabled"
          :native-value="true"
          name="enable-join-code"
        >
          Yes, enable join code
        </b-radio>
        <b-radio
          v-model="accountDetails.selfRegistration.isJoinCodeEnabled"
          :native-value="false"
          name="enable-join-code"
        >
          No, do not enable join code
        </b-radio>
        <div
          v-if="
            accountDetails.selfRegistration.joinCode &&
              accountDetails.selfRegistration.isJoinCodeEnabled
          "
        >
          <b-tag class="mt-2 mr-4 is-medium">
            Account join code:
            <strong>
              <em>{{ accountDetails.selfRegistration.joinCode }}</em>
            </strong>
          </b-tag>
          <b-tag class="mt-2 is-medium">
            Join link:
            <strong>
              <em
                >https://ganttravel.tripcircuit.com/register/{{
                  accountDetails.selfRegistration.joinCode
                }}</em
              >
            </strong>
          </b-tag>
        </div>
      </FormLayoutSection>
      <FormLayoutSection
        v-if="accountDetails.selfRegistration.isEnabled"
        :title="titles.enableEmailDomains"
        subtitle="Enable this option if all of your travelers will use email addresses with the same domain name(s)."
      >
        <b-radio
          v-model="accountDetails.selfRegistration.isEmailDomainEnabled"
          :native-value="true"
          name="enable-email-domains"
        >
          Enable email domains
        </b-radio>
        <b-radio
          v-model="accountDetails.selfRegistration.isEmailDomainEnabled"
          :native-value="false"
          name="enable-email-domains"
        >
          Do not enable email domains
        </b-radio>
      </FormLayoutSection>
      <FormLayoutSection
        v-if="
          accountDetails.selfRegistration.isEnabled &&
            accountDetails.selfRegistration.isEmailDomainEnabled
        "
        :title="titles.emailDomains"
        :description="descriptions.disallowedDomains"
      >
        <b-taginput
          v-model="accountDetails.selfRegistration.emailDomains"
          :before-adding="checkForValidDomains"
          ellipsis
          placeholder="Add an email domain. eg. companyname.com"
          aria-close-label="Delete this email domain"
        >
        </b-taginput>
      </FormLayoutSection>
    </div>
  </FormLayout>
</template>

<script>
import FormLayout from "@/layouts/FormLayout.vue";
import FormLayoutSection from "@/layouts/FormLayoutSection.vue";
import TagAutocomplete from "@/components/Tags/TagAutocomplete.vue";

export default {
  components: {
    FormLayout,
    TagAutocomplete,
    FormLayoutSection,
  },
  data() {
    return {
      isLoading: false,
      isAlertShown: false,
      travelPolicyList: [],
      accountDetails: {
        accountId: "",
        preferredAirVendors: [],
        preferredCarVendors: [],
        preferredHotelVendors: [],
        allowPersonalTravel: false,
        selectPaidSeats: '',
        selfRegistration: {
          isEnabled: false,
          emailDomains: [],
          isJoinCodeEnabled: false,
          defaultTravelPolicyId: "",
          isEmailDomainEnabled: false,
        },
      },
      selectedPreferredAirVendors: [],
      selectedPreferredCarVendors: [],
      selectedPreferredHotelVendors: [],
      titles: {
        emailDomains: "Allowed email domains",
        enableJoinCode:
          "Do you want to allow users to join your organization using a join code?",
        preferredAirVendors: "Preferred airlines",
        preferredHotelVendors: "Preferred hotels",
        enableEmailDomains:
          "Do you want to allow users with specific email domain names to join your organization?",
        defaultTravelPolicy: "Default travel policy for self-registered users",
        enableSelfRegistration:
          "Do you want to allow users to self-register to join your organization?",
        preferredCarVendors: "Preferred rental car companies",
        allowPersonalTravel:
          "Do you want to allow your organization's travelers to book personal travel using TripCircuit?",
        selectPaidSeats: 'Do you want to allow your organization\'s travelers to select paid seats for corporate travel?',
      },
      descriptions: {
        allowPersonalTravel: {
          intro:
            "When booking personal travel on TripCircuit the following applies:",
          items: [
            "No travel policies are applied.",
            "No unused tickets will be presented for use.",
            "No central corporate ghost card will be available for use. Only payment methods associated with the traveler's profile will be available for use.",
          ],
        },
        preferredVendors: {
          intro: "Setting your preferred vendors allows the following:",
          items: [
            "The ability to check for preferred vendor status is travel policy rules.",
            "Highlighting preferred vendors in search results.",
            "Filtering results by preferred vendor status.",
          ],
        },
        selectPaidSeats: {
                    intro: 'When booking corporate travel on TripCircuit the following applies:',
                    items: [
                        'Allow  Traveler can select paid seats for corporate travel.',
                        'Disallow - Traveler cannot select paid seats for corporate travel.',
                    ]
                },
        disallowedDomains: {
          intro:
            " Public email domains such as gmail.com,yahoo.com are disallowed for self-registration.",
        },
      },
    };
  },
  computed: {
    airlines() {
      return Object.entries(this.$globals.vendors.airlines);
    },
    accountId() {
      return this.$globals.currentTravellerProfile.account.id;
    },
    hotelChains() {
      return Object.entries(this.$globals.vendors.hotelChains);
    },
    carProviders() {
      return Object.entries(this.$globals.vendors.carProviders);
    },
  },
  async created() {
    this.accountDetails = await this.getAccountDetails();
    this.travelPolicyList = await this.getTravelPolicyList();

    this.isLoading = true;

    const {
      preferredAirVendors,
      preferredCarVendors,
      preferredHotelVendors,
    } = this.accountDetails;

    this.selectedPreferredAirVendors = this.airlines.filter((airline) =>
      preferredAirVendors.includes(airline[0])
    );

    this.selectedPreferredCarVendors = this.carProviders.filter((carProvider) =>
      preferredCarVendors.includes(carProvider[0])
    );

    this.selectedPreferredHotelVendors = this.hotelChains.filter((hotelChain) =>
      preferredHotelVendors.includes(hotelChain[0])
    );

    this.isLoading = false;
  },
  methods: {
    checkForValidDomains(newDomain) {
      if (this.isAlertShown) {
        // If an alert is already shown, prevent further execution. Added cause function running twice when adding alert box
        return false;
      }

      if (!this.validateDomainFormat(newDomain)) {
        this.showAlert(newDomain + " is not a valid domain format.");
        return false;
      }

      if (!this.isDomainAllowed(newDomain)) {
        this.showAlert(newDomain + " is not an allowed email domain.");
        return false;
      }

      return true;
    },

    validateDomainFormat(domain) {
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*(-\.)?[a-zA-Z]{2,}$(?<!-)/;
      return !domainRegex.test(domain);
    },
    isDomainAllowed(domain) {
      // Check if the domain is not in the disallowed list
      const disallowedDomains = this.$store.state.options
        .disallowedEmailDomains;

      return !disallowedDomains.includes(domain);
    },

    showAlert(message) {
      this.isAlertShown = true;
   

        this.$nextTick(() => {
        this.$buefy.dialog.alert({
          title: "Error",
          message: message,
          type: "is-danger",
          hasIcon: true,
          icon: "alert-circle-outline",
          iconPack: "mdi",
          ariaRole: "alertdialog",
          ariaModal: true,
          // Reset the flag when the alert is closed.
          onConfirm: () => {
            this.isAlertShown = false;
          }
        });
      });

        
    },

    async getTravelPolicyList() {
      this.isLoading = true;

      const data = { accountId: this.accountId };
      const response = await this.$app.huddleLabsServerCall(
        "profile/travel-policy/list",
        data
      );

      this.isLoading = false;

      return response.data;
    },
    async getAccountDetails() {
      this.isLoading = true;

      const data = { accountId: this.accountId };
      const response = await this.$app.huddleLabsServerCall(
        "profile/account",
        data
      );
      const { id, name, ...accountDetails } = response.data;

      accountDetails.selfRegistration = this.getFormattedSelfRegistration(
        accountDetails
      );

      this.isLoading = false;

      return { accountId: this.accountId, ...accountDetails };
    },
    async setAccountDetails() {
      this.isLoading = true;

      const data = this.accountDetails;
      const result = await this.$app.huddleLabsServerCall(
        "profile/account/save",
        data
      );

      this.isLoading = false;

      // Check if the errors array is present and has entries
  if (result.errors && result.errors.length > 0) {
    // Concatenate all error messages
    const errorMessage = result.errors.map(error => error.title).join(', ');

    this.$buefy.toast.open({
      message: errorMessage, // Display the specific error messages
      type: "is-danger",
    });
  } else {
    await this.getAccountDetails();

    this.$buefy.toast.open({
      message: "Settings saved.",
      type: "is-success",
    });
  }
    },
    updatePreferredAirVendors(preferredAirVendors) {
      this.accountDetails.preferredAirVendors = preferredAirVendors.map(
        (preferredAirVendor) => preferredAirVendor[0]
      );
    },
    updatePreferredCarVendors(preferredCarVendors) {
      this.accountDetails.preferredCarVendors = preferredCarVendors.map(
        (preferredCarVendor) => preferredCarVendor[0]
      );
    },
    updatePreferredHotelVendors(preferredHotelVendors) {
      this.accountDetails.preferredHotelVendors = preferredHotelVendors.map(
        (preferredHotelVendor) => preferredHotelVendor[0]
      );
    },
    getFormattedSelfRegistration({ selfRegistration }) {
      const strings = ["joinCode", "defaultTravelPolicyId"];
      const booleans = [
        "isEnabled",
        "isJoinCodeEnabled",
        "isEmailDomainEnabled",
      ];

      for (const property in selfRegistration) {
        const isString = strings.includes(property);
        const isBoolean = booleans.includes(property);
        const isNull = selfRegistration[property] === null;

        if (isNull && isString) selfRegistration[property] = "";
        if (isNull && isBoolean) selfRegistration[property] = false;
      }

      return selfRegistration;
    },
  },
};
</script>

<style scoped>
.section-border {
  padding: 1.25rem;
  border-radius: 8px;
  border: 10px solid #e6e6e6;
}
</style>
